import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Brush, CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

import { Settings } from '@mui/icons-material';
import PlotSettings from '../PlotSettings';
import ECSTooltip from './ECSTooltip';

const colors = {
  yellow: '#F7E300',
  brown: '#605230',
  blue: '#00A1F1',
  green: '#00A651',
  red: '#BA0000',
  orange: '#FE8484',
  purple: '#6E1E6E',
  white: '#8c8c8c',
  black: '#000000',
};

let firstTime = true;

const CHART_HEIGHT = 400;
const CHART_WIDTH = 660;
const COMPACT_HEIGHT = 280;

export default function EcsSuit({
  data = [],
  hideSettings = false,
  compact = false,
}) {
  const last = data.at(-1);

  const [sortedDataByColor, setSortedDataByColor] = useState({});
  const [selectedColor, selectColor] = useState('');

  const [yMin, setYMin] = useState(0);
  const [yMax, setYMax] = useState(200);
  const [btuTickCount, setBTUTickCount] = useState(15);

  const [chartWindowMinNum, setChartWindowMinNum] = useState(0);

  const [start, setStartIndex] = useState(0);
  const [end, setEndIndex] = useState(0);

  const [settingsOpen, setSettingsOpen] = useState(false);

  const [activeKeys, setActiveKeys] = useState({
    coolingFlow: true,
    emuDepth: true,
    returnGasFlowScfm: true,
    suitActualPressure: true,
    suitAmbientPressure: true,
    suitDeltaPressure: true,
    supplyGasFlowScfm: true,
    testStartTime: false,
    totalGasOnTimeInSec: true,
    waterSupplyTemp: true,
  });

  const closeSettings = () => setSettingsOpen(false);

  const handleBrushUpdate = ({
    startIndex,
    endIndex,
  }) => {
    setStartIndex(startIndex);
    setEndIndex(((data.length - 1) - endIndex <= 1) ? null : endIndex);
  };

  const handleColorChange = (e) => {
    selectColor(e.target.value);
  };

  const togglePlotSettings = () => setSettingsOpen(!settingsOpen);

  const toggleActiveKeys = (key) => {
    setActiveKeys((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const renderLines = () => {
    const lines = [];

    if (sortedDataByColor[selectedColor] && sortedDataByColor[selectedColor].length) {
      Object.keys(activeKeys).forEach((key) => {
        const excludedKeys = ['eventType', 'testStartTime', 'timestamp'];
        if (activeKeys[key] && excludedKeys.indexOf(key) === -1) {
          lines.push(
            <Line
              key={key}
              dataKey={key}
              stroke={colors[selectedColor]}
              dot={false}
              isAnimationActive
              type="monotone"
            />,
          );
        }
      });
    }

    return lines;
  };

  useEffect(() => {
    const updatedSortedDataByColor = data.reduce((accumulator, currentEntry) => {
      const color = currentEntry.umbilicalColor.toLowerCase();
      if (firstTime) {
        firstTime = false;
        selectColor(color);
      }
      if (!accumulator[color]) {
        accumulator[color] = [];
      }
      accumulator[color].push(currentEntry);
      return accumulator;
    }, {});

    setSortedDataByColor(updatedSortedDataByColor);
  }, [data]);

  return (
    <div className={`ecs-suit plot${compact ? ' compact' : ''}`}>
      <div className="header">
        <span>ECS Suit Data</span>
        {!hideSettings && (
        <div className="settings-controller">
          <Settings data-testid="ecs-suit-plot-settings" onClick={togglePlotSettings} />
          {settingsOpen
            ? (
              <PlotSettings
                setYMin={setYMin}
                startYMin={yMin}
                setYMax={setYMax}
                startYMax={yMax}
                setTickCount={setBTUTickCount}
                startTickCount={btuTickCount}
                setChartWindow={setChartWindowMinNum}
                startChartWindow={chartWindowMinNum}
                ecsSuitIsActive={activeKeys}
                handleEcsSuitChange={toggleActiveKeys}
                selectedColor={selectedColor}
                availableColors={Object.keys(sortedDataByColor)}
                handleColorChange={handleColorChange}
              />
            ) : null}
        </div>
        )}
      </div>
      {activeKeys?.testStartTime
        ? (
          <div className="info flex-row">
            {`Test Start Time: ${last?.testStartTime || 'Not Available'}`}
          </div>
        ) : null}
      <div className="ecs-suit-chart-container" onClick={closeSettings} role="presentation">
        {compact ? (
          <ResponsiveContainer width="100%" height={COMPACT_HEIGHT}>
            <LineChart data={data}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="timestamp" interval="preserveStartEnd" />
              <YAxis
                domain={[Math.max((yMin), 0), yMax]}
                interval={0}
                tickCount={btuTickCount}
                tickFormatter={Math.round}
                allowDataOverflow
                type="number"
              />
              <Tooltip content={<ECSTooltip activeKeys={activeKeys} />} />
              {renderLines()}
              {(data && data.length)
                ? (
                  <Brush
                    data={data}
                    dataKey="timestamp"
                    height={compact ? 24 : 40}
                    padding={{
                      left: 0,
                      right: 10,
                    }}
                    tick
                    startIndex={start || (chartWindowMinNum ? Math.max(data.length - 60 * chartWindowMinNum, 0) : 0)}
                    endIndex={end || (data.length - 1)}
                    onChange={handleBrushUpdate}
                    travellerWidth={6}
                  />
                ) : null}
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <LineChart data={data} height={CHART_HEIGHT} width={CHART_WIDTH}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="timestamp" interval="preserveStartEnd" />
            <YAxis
              domain={[Math.max((yMin), 0), yMax]}
              interval={0}
              tickCount={btuTickCount}
              tickFormatter={Math.round}
              allowDataOverflow
              type="number"
            />
            <Tooltip content={<ECSTooltip activeKeys={activeKeys} />} />
            {renderLines()}
            {(data && data.length)
              ? (
                <Brush
                  data={data}
                  dataKey="timestamp"
                  height={40}
                  padding={{
                    left: 0,
                    right: 10,
                  }}
                  tick
                  startIndex={start || (chartWindowMinNum ? Math.max(data.length - 60 * chartWindowMinNum, 0) : 0)}
                  endIndex={end || (data.length - 1)}
                  onChange={handleBrushUpdate}
                  travellerWidth={6}
                />
              ) : null}
          </LineChart>
        )}
      </div>
    </div>
  );
}

EcsSuit.propTypes = {
  data: PropTypes.any,
  hideSettings: PropTypes.bool,
  compact: PropTypes.bool,
};
